/* ============================================================
 * Validar el pase en el mostrador.
 *
 * La entrada manual es la vía principal, no el respaldo: siempre funciona,
 * en cualquier navegador y sin permisos. La cámara se ofrece encima cuando
 * el navegador la soporta, porque es más rápida cuando va bien — pero un
 * control de acceso que dependa de ella falla justo el día que hay cola.
 *
 * Se usa BarcodeDetector, nativo del navegador, en lugar de una librería
 * de lectura de QR. Donde no existe, queda el código escrito, que es lo
 * que de verdad sostiene la función.
 * ============================================================ */

import React, { useState, useRef, useEffect } from 'react';
import { useCtx, useAccion, api } from '../estado.jsx';

const HAY_CAMARA = typeof window !== 'undefined'
  && 'BarcodeDetector' in window
  && navigator.mediaDevices?.getUserMedia;

export function ValidarView() {
  const { refrescar } = useCtx();
  const { ejecutar, enCurso } = useAccion();
  const [clave, setClave] = useState('');
  const [resultado, setResultado] = useState(null);
  const [leyendo, setLeyendo] = useState(false);
  const video = useRef(null);
  const flujo = useRef(null);

  const validar = (valor) => {
    if (!valor.trim()) return;
    ejecutar(() => api.validarPase(valor.trim()), (salida) => {
      setResultado(salida);
      setClave('');
      refrescar();          // el tablero ya refleja la sesión como cumplida
    });
  };

  /* ---------- cámara ---------- */

  const pararCamara = () => {
    if (flujo.current) {
      flujo.current.getTracks().forEach((t) => t.stop());
      flujo.current = null;
    }
    setLeyendo(false);
  };

  // La cámara se apaga al salir de la pantalla. Dejarla encendida enciende
  // el piloto del dispositivo y consume batería sin que nadie lo note.
  useEffect(() => pararCamara, []);

  const empezarCamara = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      flujo.current = s;
      if (video.current) {
        video.current.srcObject = s;
        await video.current.play();
      }
      setLeyendo(true);

      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const buscar = async () => {
        if (!flujo.current) return;
        try {
          const codigos = await detector.detect(video.current);
          if (codigos.length) {
            // El QR lleva la URL del pase; interesa el token del final.
            const bruto = codigos[0].rawValue || '';
            const token = bruto.split('/').pop();
            pararCamara();
            validar(token);
            return;
          }
        } catch { /* fotograma ilegible: se intenta con el siguiente */ }
        requestAnimationFrame(buscar);
      };
      requestAnimationFrame(buscar);
    } catch {
      setResultado({ ok: false, motivo: 'No se ha podido abrir la cámara. Escribe el código.' });
      setLeyendo(false);
    }
  };

  return (
    <div className="view on">
      <div className="h-eyebrow">Mostrador</div>
      <h1 className="h-title">Validar un <em>pase</em></h1>
      <p className="h-sub">
        Escanea el QR del cliente o escribe su código. Al validarlo, la sesión
        queda registrada como cumplida.
      </p>

      <div className="frow" style={{ maxWidth: 520, alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Código del pase</label>
          <input className="inp" value={clave} autoFocus
                 placeholder="ABC-123"
                 style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                          fontSize: 20, letterSpacing: '.06em' }}
                 onChange={(e) => setClave(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') validar(clave); }} />
        </div>
        <button className="btn" onClick={() => validar(clave)} disabled={enCurso || !clave.trim()}>
          {enCurso ? 'Comprobando…' : 'Validar'}
        </button>
      </div>

      {HAY_CAMARA ? (
        <div style={{ marginTop: 18 }}>
          {!leyendo
            ? <button className="btn ghost" onClick={empezarCamara}>Escanear con la cámara</button>
            : (
              <div style={{ maxWidth: 380 }}>
                <video ref={video} playsInline muted
                       style={{ width: '100%', border: '1px solid var(--line)' }} />
                <button className="btn ghost" style={{ marginTop: 10 }}
                        onClick={pararCamara}>Apagar la cámara</button>
              </div>
            )}
        </div>
      ) : (
        <p className="section-note" style={{ marginTop: 14 }}>
          Este navegador no puede leer códigos QR. Escribe el código del pase,
          que funciona igual.
        </p>
      )}

      {resultado ? <Resultado resultado={resultado} alCerrar={() => setResultado(null)} /> : null}
    </div>
  );
}

function Resultado({ resultado, alCerrar }) {
  const { ok, motivo, reserva } = resultado;
  return (
    <div className="card" style={{
      marginTop: 26, padding: 22, maxWidth: 520,
      borderLeft: `3px solid ${ok ? 'var(--pos)' : 'var(--err)'}`,
    }}>
      <div style={{ fontSize: 19, fontWeight: 600, marginBottom: 6 }}>
        {ok ? '✓ Pase válido' : '✗ Pase no válido'}
      </div>

      {ok ? (
        <>
          <div style={{ fontSize: 24, marginBottom: 4 }}>{reserva.cliente}</div>
          <div className="section-note" style={{ margin: 0 }}>
            {reserva.hora} · {reserva.equipo} · sesión registrada como cumplida
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: reserva ? 10 : 0 }}>{motivo}</div>
          {/* Cuando el pase existe pero no vale, se muestra de quién es: quien
              atiende necesita poder explicarle al cliente qué ha pasado. */}
          {reserva ? (
            <div className="section-note" style={{ margin: 0 }}>
              {reserva.cliente} · {reserva.hora} · {reserva.equipo}
            </div>
          ) : null}
        </>
      )}

      <button className="btn ghost" style={{ marginTop: 16 }} onClick={alCerrar}>
        Validar otro
      </button>
    </div>
  );
}
